import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, errMsg } from "@/lib/api";
import { useSubscription } from "@/hooks/useSubscription";
import { PaywallModal } from "@/components/PaywallModal";
import { StatusBadge } from "@/components/StatusBadge";
import { formatPrice, formatTimeAgo } from "@/lib/utils-app";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import { Zap, ShieldAlert, Wallet, Clock, Loader2, ChevronRight } from "lucide-react";

export function ProDashboard() {
  const qc = useQueryClient();
  const [duration, setDuration] = useState("120");
  const [busy, setBusy] = useState(false);
  const [paywall, setPaywall] = useState(false);
  const { isPro, isLoading: subLoading } = useSubscription();

  const { data: profile, isLoading: loadingProfile } = useQuery({
    queryKey: ["pro-profile"],
    queryFn: async () => (await api.get("/pro/profile")).data,
  });

  const { data: availability } = useQuery({
    queryKey: ["pro-availability"],
    queryFn: async () => (await api.get("/pro/availability")).data,
  });

  const { data: earnings } = useQuery({
    queryKey: ["pro-earnings"],
    queryFn: async () => (await api.get("/pro/earnings")).data,
  });

  const { data: offers } = useQuery({
    queryKey: ["pro-offers"],
    queryFn: async () => (await api.get("/pro/offers")).data,
  });

  const setAvailable = async (on) => {
    if (on && subLoading) return;
    if (on && !isPro) { setPaywall(true); return; }
    setBusy(true);
    try {
      if (on) {
        const pos = await new Promise((res, rej) => navigator.geolocation.getCurrentPosition(res, rej, { enableHighAccuracy: true, timeout: 10000 }));
        await api.post("/pro/availability", {
          duration_minutes: Number(duration),
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
        });
        toast.success("Sei ora Disponibile ora! Riceverai richieste vicine.");
      } else {
        await api.delete("/pro/availability");
        toast.success("Disponibilità disattivata");
      }
      qc.invalidateQueries({ queryKey: ["pro-availability"] });
    } catch (err) {
      toast.error(err.code ? "Serve il permesso di geolocalizzazione per attivarti" : errMsg(err));
    } finally { setBusy(false); }
  };

  if (loadingProfile) return <div className="px-4 pt-6 space-y-3"><Skeleton className="h-32 rounded-3xl" /><Skeleton className="h-24 rounded-3xl" /></div>;

  if (profile && !profile.onboarding_complete) {
    return (
      <div className="px-4 pt-10 space-y-4 text-center" data-testid="onboarding-needed">
        <h1 className="font-heading text-2xl font-bold text-slate-900">Completa il tuo profilo</h1>
        <p className="text-sm text-slate-600">Per ricevere richieste devi completare l'onboarding da professionista.</p>
        <Link to="/pro/onboarding">
          <Button data-testid="go-onboarding-btn" className="rounded-full bg-whoo hover:bg-whoo-dark text-white font-semibold active:scale-95 transition-transform">
            Completa onboarding <ChevronRight className="w-4 h-4 ml-1" />
          </Button>
        </Link>
      </div>
    );
  }

  const pendingVerify = profile && profile.verification_status !== "verified" && profile.verification_status !== "approvato";
  const active = availability?.active;

  return (
    <div className="px-4 sm:px-6 pt-6 sm:pt-10 space-y-5 sm:max-w-3xl sm:mx-auto" data-testid="pro-dashboard">
      <h1 className="font-heading text-2xl font-bold tracking-tight text-slate-900">Dashboard</h1>

      {pendingVerify && (
        <div className="bg-amber-50 border border-amber-200 rounded-3xl p-4 flex gap-3" data-testid="verification-banner">
          <ShieldAlert className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-semibold text-amber-800">Profilo non ancora operativo</p>
            <p className="text-xs text-amber-700 leading-relaxed">Verifica il numero di telefono e attendi l'approvazione di un amministratore per iniziare a lavorare.</p>
          </div>
        </div>
      )}

      <div className={`rounded-3xl p-5 space-y-4 border ${active ? "bg-whoo text-white border-whoo" : "bg-white border-slate-100 shadow-sm"}`} data-testid="availability-card">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className={`w-11 h-11 rounded-2xl flex items-center justify-center ${active ? "bg-white/20" : "bg-whoo-soft text-whoo"}`}>
              <Zap className="w-5 h-5" />
            </span>
            <div>
              <p className={`font-heading font-semibold ${active ? "text-white" : "text-slate-900"}`}>Disponibile ora</p>
              <p className={`text-xs ${active ? "text-orange-100" : "text-slate-500"}`}>
                {active ? "Attivo: ricevi richieste in tempo reale" : "Attivati per ricevere richieste vicino a te"}
              </p>
            </div>
          </div>
          {busy ? <Loader2 className="w-6 h-6 animate-spin" /> : (
            <Switch data-testid="availability-switch" checked={!!active} onCheckedChange={setAvailable} disabled={subLoading} />
          )}
        </div>
        {!active && (
          <div className="flex items-center gap-2">
            <Clock className="w-4 h-4 text-slate-400" />
            <Select value={duration} onValueChange={setDuration}>
              <SelectTrigger data-testid="availability-duration" className="h-10 bg-slate-50 border-transparent rounded-xl text-sm flex-1">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="60">Per 1 ora</SelectItem>
                <SelectItem value="120">Per 2 ore</SelectItem>
                <SelectItem value="240">Per 4 ore</SelectItem>
                <SelectItem value="480">Per 8 ore</SelectItem>
              </SelectContent>
            </Select>
          </div>
        )}
        {active && availability?.session?.expires_at && (
          <p className="text-xs text-orange-100">Attivo fino a {new Date(availability.session.expires_at).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" })}</p>
        )}
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div className="bg-white rounded-3xl border border-slate-100 shadow-sm p-4" data-testid="earnings-card">
          <Wallet className="w-5 h-5 text-emerald-600 mb-2" />
          <p className="font-heading text-xl font-bold text-slate-900">{formatPrice(earnings?.total ?? earnings?.total_earnings ?? 0)}</p>
          <p className="text-xs text-slate-500">Guadagni totali</p>
        </div>
        <div className="bg-white rounded-3xl border border-slate-100 shadow-sm p-4" data-testid="jobs-count-card">
          <Zap className="w-5 h-5 text-whoo mb-2" />
          <p className="font-heading text-xl font-bold text-slate-900">{profile?.completed_jobs ?? 0}</p>
          <p className="text-xs text-slate-500">Lavori completati</p>
        </div>
      </div>

      <section className="space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="font-heading text-lg font-semibold text-slate-900">Le mie offerte</h2>
          <div className="flex items-center gap-3">
            <Link to="/pro/abbonamento" className="text-xs font-semibold text-whoo" data-testid="go-subscription-link">Il mio abbonamento</Link>
            <Link to="/pro/feed" className="text-xs font-semibold text-volt" data-testid="go-feed-link">Trova richieste</Link>
          </div>
        </div>
        {!offers?.length ? (
          <p className="text-sm text-slate-500" data-testid="no-offers-sent">Non hai ancora inviato offerte.</p>
        ) : (
          <div className="grid gap-3 sm:grid-cols-2" data-testid="my-offers-list">
            {offers.map((o) => (
              <div key={o.id} data-testid={`my-offer-${o.id}`} className="bg-white rounded-3xl border border-slate-100 shadow-sm p-4 flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <p className="font-semibold text-slate-900 text-sm truncate">{o.request_title || "Richiesta"}</p>
                  <p className="text-xs text-slate-500">{formatPrice(o.price)} · {formatTimeAgo(o.created_at)}</p>
                </div>
                <StatusBadge status={o.status} testid={`my-offer-status-${o.id}`} />
              </div>
            ))}
          </div>
        )}
      </section>

      <PaywallModal open={paywall} onClose={() => setPaywall(false)} reason={'Attivare "Disponibile ora" è una funzione Pro.'} />
    </div>
  );
}
